import React, { useEffect, useState } from "react";

import { Select } from "antd";
import { clientService, statsService, userService } from "services";
import { history, selectProps, serialize } from "utils";
import SmallLine from "./_charts/SmallLine";

const GRAPHS = {
  0: "$ Soumissions par mois",
  1: "$ Obtenus par mois",
  2: "Nb. soumissions par mois",
};

const TYPES = [
  { id: 1, label: "Prospection" },
  { id: 2, label: "En soumission" },
  { id: 3, label: "En attente" },
  { id: 7, label: "En discussion" },
  { id: 4, label: "Obtenu" },
  { id: 5, label: "Non obtenu" },
  { id: 6, label: "Annulé" },
];

const EMPTY_GRAPHS = {
  amount_estimate_by_month_by_year: [],
  amount_real_by_month_by_year: [],
  submission_by_month_by_year: [],
};

const FILTER_KEYS = ["client_id", "submission_state_id", "user_id", "created_year"];

const filtersFromSearch = (search) => {
  const filters = {};

  for (const [rawKey, value] of new URLSearchParams(search).entries()) {
    const key = rawKey.replace(/\[\]$/, "");
    if (!FILTER_KEYS.includes(key) || value === "") continue;

    filters[key] = [...(filters[key] || []), /^\d+$/.test(value) ? Number(value) : value];
  }

  return filters;
};

const defaultFilters = () => ({
  created_year: [
    new Date().getFullYear(),
    new Date().getFullYear() - 1,
    new Date().getFullYear() - 2,
  ],
});

const getYears = () => {
  const currentYear = new Date().getFullYear();
  const years = [];
  let startYear = 2015;
  while (startYear <= currentYear) {
    years.push(startYear++);
  }
  return years;
};

const SubmissionsGraphs = ({ filtersAlwaysOpen = false, syncFiltersToUrl = false }) => {
  const [graphs, setGraphs] = useState(EMPTY_GRAPHS);
  const [filters, setFilters] = useState(() => {
    if (!syncFiltersToUrl) return defaultFilters();

    const fromUrl = filtersFromSearch(history.location.search);
    return Object.keys(fromUrl).length > 0 ? fromUrl : defaultFilters();
  });
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [filterOpened, setFilterOpened] = useState(false);

  useEffect(() => {
    let cancelled = false;

    Promise.all([
      clientService.list({ order_by: "name", order_direction: "ASC" }),
      userService.list({ order_by: "name", order_direction: "ASC", type_id: [1, 2] }),
    ]).then(([clients, users]) => {
      if (cancelled) return;
      setClients(clients || []);
      setUsers(users || []);
    });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!syncFiltersToUrl) return;

    history.replace({ pathname: history.location.pathname, search: serialize(filters) });
  }, [filters, syncFiltersToUrl]);

  useEffect(() => {
    if (!syncFiltersToUrl) return;

    const { pathname } = history.location;
    return history.listen((location) => {
      if (location.pathname !== pathname) return;

      // Fires for our own replace above too; bail on equal filters or it loops.
      const next = filtersFromSearch(location.search);
      setFilters((current) => (serialize(current) === serialize(next) ? current : next));
    });
  }, [syncFiltersToUrl]);

  useEffect(() => {
    let cancelled = false;

    statsService
      .get("/statistics/submission/", { indicator: "graph", ...filters })
      .then((response) => {
        if (cancelled) return;
        setGraphs({ ...EMPTY_GRAPHS, ...response });
      })
      .catch(() => {
        if (cancelled) return;
        setGraphs(EMPTY_GRAPHS);
      });

    return () => {
      cancelled = true;
    };
  }, [filters]);

  const handleFiltersChange = (value, key) => {
    if (key === "date") value = value.map((date) => date.format("YYYY-MM-DD"));

    if (value.length > 0) setFilters({ ...filters, [key]: value });
    else {
      const tmpFilters = { ...filters };
      delete tmpFilters[key];
      setFilters(tmpFilters);
    }
  };

  const renderFilters = () => (
    <div className="ctn">
      {!filtersAlwaysOpen && (
        <button onClick={() => setFilterOpened(false)}>
          <span>{Object.keys(filters).length}</span> Fermer
        </button>
      )}
      <label>Client :</label>
      <Select
        {...selectProps()}
        mode="multiple"
        value={filters["client_id"]}
        style={{ minWidth: 256 }}
        onChange={(val) => handleFiltersChange(val, "client_id")}
      >
        {clients.map((p, i) => (
          <Select.Option key={i} value={p.id}>
            {p.name}
          </Select.Option>
        ))}
      </Select>
      <label>Type :</label>
      <Select
        {...selectProps()}
        mode="multiple"
        value={filters["submission_state_id"]}
        style={{ minWidth: 256 }}
        onChange={(val) => handleFiltersChange(val, "submission_state_id")}
      >
        {TYPES.map((p, i) => (
          <Select.Option key={`${i}-${p.id}`} value={p.id}>
            {p.label}
          </Select.Option>
        ))}
      </Select>
      <label>Estimateur :</label>
      <Select
        {...selectProps()}
        mode="multiple"
        value={filters["user_id"]}
        style={{ minWidth: 256 }}
        onChange={(val) => handleFiltersChange(val, "user_id")}
      >
        {users.map((p, i) => (
          <Select.Option key={`${i}-${p.id}`} value={p.id}>
            {p.name}
          </Select.Option>
        ))}
      </Select>
      <label>Année :</label>
      <Select
        {...selectProps()}
        mode="multiple"
        value={filters["created_year"]}
        style={{ minWidth: 256 }}
        defaultValue={filters["created_year"]}
        onChange={(val) => handleFiltersChange(val, "created_year")}
      >
        {getYears().map((p, i) => (
          <Select.Option key={`${i}-${p}`} value={p}>
            {p}
          </Select.Option>
        ))}
      </Select>
    </div>
  );

  return (
    <div className="graphs">
      <div className="charts">
        {Object.values(graphs).map((graph, j) => (
          <React.Fragment key={j}>
            <p className="graph-title">{GRAPHS[j]}</p>
            <SmallLine graph={graph} filters={filters} />
          </React.Fragment>
        ))}
      </div>
      <div className="filters">
        {filtersAlwaysOpen || filterOpened ? (
          renderFilters()
        ) : (
          <button onClick={() => setFilterOpened(true)}>
            <span>{Object.keys(filters).length}</span> Filtres
          </button>
        )}
      </div>
    </div>
  );
};

export default SubmissionsGraphs;
